import React from 'react';
import { ArrowRight } from 'lucide-react';

export type PreviewSection = 'popular_tasks' | 'sample_prompts' | 'pairs_with';

export interface PreviewHighlight {
  section: PreviewSection;
  index: number;
}

interface BuddyOverviewPreviewProps {
  buddyName: string;
  icon: string;
  popularTasks: string[];
  samplePrompts: string[];
  pairsWith: string[];
  /** Item currently focused/hovered in the form, highlighted here. */
  highlight?: PreviewHighlight | null;
}

const IMAGE_URL_RE = /^(https?:\/\/|\/|data:image\/)/i;

/** Slugs (`sous-chef`) are what the config is keyed by, but the Overview page
 *  shows a human title, so mirror that here. */
const prettifyName = (name: string): string =>
  name
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((word) =>
      /[A-Z]/.test(word.slice(1)) ? word : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(' ');

const possessive = (name: string): string =>
  name.endsWith('s') ? `${name}’` : `${name}’s`;

const BuddyIcon: React.FC<{ icon: string }> = ({ icon }) => {
  const trimmed = icon.trim();
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-indigo-50">
      {!trimmed ? (
        <span className="text-xs text-gray-300">?</span>
      ) : IMAGE_URL_RE.test(trimmed) ? (
        <img src={trimmed} alt="" className="h-full w-full object-cover" />
      ) : (
        <span className="text-xl leading-none">{trimmed}</span>
      )}
    </div>
  );
};

const EmptyHint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-xs text-gray-400">
    {children}
  </div>
);

const highlightRing = (active: boolean) =>
  active ? 'ring-2 ring-indigo-400 ring-offset-1' : '';

/**
 * Read-only replica of how the buddy's Overview page renders this config, so
 * the admin can see the result while editing. Purely presentational — it never
 * reads from the API, only from the in-progress form state.
 */
const BuddyOverviewPreview: React.FC<BuddyOverviewPreviewProps> = ({
  buddyName,
  icon,
  popularTasks,
  samplePrompts,
  pairsWith,
  highlight,
}) => {
  const title = possessive(prettifyName(buddyName));
  const tasks = popularTasks.map((t) => t.trim());
  const prompts = samplePrompts.map((p) => p.trim());
  const pairs = pairsWith.map((p) => p.trim()).filter(Boolean);

  const isActive = (section: PreviewSection, index: number) =>
    highlight?.section === section && highlight.index === index;

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <div className="rounded-2xl border border-gray-200 bg-white p-5">
        <div className="mb-4 flex items-center gap-3">
          <BuddyIcon icon={icon} />
          <h3 className="text-base font-semibold text-gray-900">
            {title} Most Popular Tasks
          </h3>
        </div>

        {tasks.length === 0 ? (
          <EmptyHint>Popular tasks appear here as cards.</EmptyHint>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {tasks.map((task, index) => (
              <div
                key={index}
                className={`flex items-center justify-center rounded-xl border border-gray-200 bg-gray-50 px-3 py-4 text-center text-sm leading-snug text-gray-700 transition-shadow ${highlightRing(
                  isActive('popular_tasks', index)
                )}`}
              >
                {task || (
                  <span className="italic text-gray-300">Empty task</span>
                )}
              </div>
            ))}
          </div>
        )}

        <p className="mt-5 text-sm text-gray-700">
          <span className="font-semibold">Pairs with</span>{' '}
          {pairs.length === 0 ? (
            <span className="italic text-gray-300">nothing yet</span>
          ) : (
            pairs.map((pair, index) => (
              <span
                key={index}
                className={`rounded px-0.5 ${
                  isActive('pairs_with', index) ? 'bg-indigo-100' : ''
                }`}
              >
                {pair}
                {index < pairs.length - 1 ? ', ' : ''}
              </span>
            ))
          )}
        </p>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-5">
        <h3 className="mb-4 text-base font-semibold text-gray-900">Sample Prompts</h3>

        {prompts.length === 0 ? (
          <EmptyHint>Sample prompts appear here with a use prompt button.</EmptyHint>
        ) : (
          <div className="flex flex-col gap-4">
            {prompts.map((prompt, index) => (
              <div key={index} className="flex flex-col items-start gap-2">
                <div
                  className={`w-full rounded-lg border border-dashed border-indigo-200 bg-white px-4 py-3 text-sm leading-snug text-gray-500 transition-shadow ${highlightRing(
                    isActive('sample_prompts', index)
                  )}`}
                >
                  {prompt || (
                    <span className="italic text-gray-300">Empty prompt</span>
                  )}
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-md bg-[#E3FF6A] px-3 py-1.5 text-xs font-medium text-[#242424]">
                  Use Prompt
                  <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BuddyOverviewPreview;
